import type { Theme } from '@goodtime/engine';
/**
 * Neon Heights: the neon-biome themed slot, named after its world. Mapping:
 * cherry→palm, lemon→disc, orange→joystick, bell→lightning,
 * star(wild)→grid_wild, seven→robot, gem→prism,
 * scatter→spark_scatter, money→holo_coin.
 */
export const NEON_THEME: Theme = {
  id: 'neon',
  name: 'Neon Heights',
  palette: {
    accent: 0xff2ea8,         // signature hot pink
    secondary: 0x42e2f5,      // cyan
    warm: 0xfff59d,           // electric yellow
    frameFill: 0x12041d,      // deep purple-black
    bgGradientTop: 0x1a0b3d,
    bgGradientBottom: 0x4a1a6b,
    dimOverlay: 0x05010f,
  },
  slot: {
    symbols: [
      { id: 'cherry',  label: 'Palm',      glyph: '🌴', tint: 0xff2ea8, weight: 5,   textureAlias: 'neon:cherry' },
      { id: 'lemon',   label: 'Disc',      glyph: '💿', tint: 0xeae5ff, weight: 5,   textureAlias: 'neon:lemon' },
      { id: 'orange',  label: 'Joystick',  glyph: '🕹️', tint: 0xff2ea8, weight: 4,   textureAlias: 'neon:orange' },
      { id: 'bell',    label: 'Lightning', glyph: '⚡', tint: 0xffd23b, weight: 3,   textureAlias: 'neon:bell' },
      { id: 'star',    label: 'Grid',      glyph: '🪩', tint: 0xff2ea8, weight: 2,   textureAlias: 'neon:star' }, // wild
      { id: 'seven',   label: 'Robot',     glyph: '🤖', tint: 0x42e2f5, weight: 1,   textureAlias: 'neon:seven' },
      { id: 'gem',     label: 'Prism',     glyph: '🔮', tint: 0xb38bff, weight: 1,   textureAlias: 'neon:gem' },
      { id: 'scatter', label: 'Spark',     glyph: '✨', tint: 0xfff59d, weight: 1,   textureAlias: 'neon:scatter' },
      { id: 'money',   label: 'Holo Coin', glyph: '$', tint: 0xc8a01f, weight: 1.5, textureAlias: 'neon:money', isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0xff2ea8, multiplier: 1,  weight: 4 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0x42e2f5, multiplier: 2,  weight: 3 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x12041d, multiplier: 0,  weight: 2 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0xb38bff, multiplier: 3,  weight: 2 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0xff2ea8, multiplier: 1,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffd23b, multiplier: 5,  weight: 1 },
      { id: 'x0-b',   kind: 'multiplier', label: '✕',   tint: 0x12041d, multiplier: 0,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xfff59d, multiplier: 10, weight: 1 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0x42e2f5, multiplier: 2,  weight: 3 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0xff2ea8, multiplier: 1,  weight: 3 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0xb38bff, multiplier: 3,  weight: 2 },
      { id: 'jackpot',kind: 'multiplier', label: '×25', tint: 0xffd700, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: {
      cherry:  { alias: 'neon:cherry',  path: 'reel/neon/palm.png' },
      lemon:   { alias: 'neon:lemon',   path: 'reel/neon/disc.png' },
      orange:  { alias: 'neon:orange',  path: 'reel/neon/joystick.png' },
      bell:    { alias: 'neon:bell',    path: 'reel/neon/lightning.png' },
      star:    { alias: 'neon:star',    path: 'reel/neon/grid_wild.png' },
      seven:   { alias: 'neon:seven',   path: 'reel/neon/robot.png' },
      gem:     { alias: 'neon:gem',     path: 'reel/neon/prism.png' },
      scatter: { alias: 'neon:scatter', path: 'reel/neon/spark_scatter.png' },
      money:   { alias: 'neon:money',   path: 'reel/neon/holo_coin.png' },
    },
    scene: {
      background:       { alias: 'neon:bg',         path: 'environment/neon.png' },
      columnBackground: { alias: 'neon:column_bg',  path: 'reel/neon_column_bg.png' },
      border:           { alias: 'neon:border',     path: 'brand/border_neon.png' },
    },
  },
};
